import { RefObject, useRef, useState } from "react";
import { useInView } from "motion/react";
import styles from "./Video.module.scss";
import { ReactSVG } from "react-svg";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { iframeSrcFetch } from "../../../utils/iframeSrcFetch";
import dynamic from "next/dynamic";
const VidstackPlayer = dynamic(() => import("./VidstackPlayer"));

import { type MediaPlayerInstance } from "@vidstack/react";
import { HeaderVideosType } from "@/types/PageType";

/* jscpd:ignore-start */
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false,
  loading: () => <></>,
});
/* jscpd:ignore-end */

export const Video = ({
  onlineVideo,
  poster,
}: {
  onlineVideo: string;
  poster?: string;
}) => {
  const video_src = iframeSrcFetch(onlineVideo);
  const { getIcon } = useGlobalContext();

  const playIcon = (
    <ReactSVG
      src={getIcon("play")}
      width={83}
      height={83}
      wrapper="svg"
      aria-hidden
      beforeInjection={(svg) => {
        svg.setAttribute("alt", `Play Icon`);
        svg.setAttribute("aria-label", `Play Icon`);
      }}
    />
  );

  return (
    <div className={`${styles["iframe-box"]} iframe-box`}>
      <ReactPlayer
        url={video_src}
        playing
        loop
        controls={true}
        // uncomment below to enable background video and disable controls
        // config={{
        //   vimeo: {
        //     playerOptions: {
        //       background: true,
        //     },
        //   },
        // }}
        light={poster || true}
        playIcon={playIcon ? playIcon : undefined}
        width={"100%"}
        height={"100%"}
        className={styles["video-player"]}
      />
    </div>
  );
};

export const AudioIcon = ({
  isMuted,
  player,
  getIcon,
}: {
  isMuted: boolean;
  player: RefObject<MediaPlayerInstance>;
  getIcon: (key: string) => string;
}) => {
  const { getLabel } = useGlobalContext();
  return (
    <button
      className={`${isMuted ? styles["muted"] : ""} ${styles["audio-btn"]} button-reset`}
      onClick={() => {
        const c = player.current;
        if (c) {
          c.muted = !isMuted;
        }
      }}
      role="switch"
      aria-checked={!isMuted}
      aria-label={
        isMuted ? getLabel("unmute", "Unmute") : getLabel("mute", "Mute")
      }
    >
      <ReactSVG
        width={32}
        height={32}
        src={isMuted ? getIcon("audioOff") : getIcon("audioOn")}
        wrapper="svg"
        beforeInjection={(svg) => {
          svg.setAttribute(
            "alt",
            isMuted ? `${getLabel("mute")} Icon` : `${getLabel("unmute")} Icon`,
          );
          svg.setAttribute(
            "aria-label",
            isMuted ? `${getLabel("mute")} Icon` : `${getLabel("unmute")} Icon`,
          );
          svg.setAttribute("aria-hidden", `true`);
        }}
        aria-hidden
      />
    </button>
  );
};

type InternalVideoProps = {
  autoplay: boolean;
  controls?: boolean;
  loop?: boolean;
  muted: boolean;
  video_decorative?: boolean;
  singlePageUrl?: string;
  video: HeaderVideosType;
};

export const InternalVideo = ({
  autoplay,
  loop = true,
  muted: mutedProps,
  video_decorative = false,
  singlePageUrl,
  video,
}: InternalVideoProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const playerInView = useInView(ref, { once: true });

  return (
    <div ref={ref}>
      {playerInView && video.internes_video_desktop && (
        <VidstackPlayer
          autoplay={autoplay}
          loop={loop}
          muted={mutedProps}
          video_decorative={video_decorative}
          singlePageUrl={singlePageUrl}
          video={video}
        />
      )}
    </div>
  );
};

export const PlayPauseButton = ({
  isPlaying,
  player,
  getIcon,
  setShowcontrols,
  video_decorative = false,
}: {
  isPlaying: boolean;
  player: React.RefObject<MediaPlayerInstance>; // Replace 'any' with your MediaPlayerInstance type if available
  getIcon: (key: string) => string;
  setShowcontrols: React.Dispatch<React.SetStateAction<boolean>>;
  video_decorative?: boolean;
}) => {
  const [playing, setPlaying] = useState(isPlaying);
  const { getLabel } = useGlobalContext();
  const handlePlayPause = () => {
    const c = player.current;
    if (c) {
      if (playing) {
        c.pause(); // Assuming your MediaPlayerInstance has a pause method
      } else {
        c.play(); // Assuming your MediaPlayerInstance has a play method
      }
      setPlaying(!playing);
      if (!video_decorative) setShowcontrols(!playing);
    }
  };

  return (
    <button
      className={`${playing ? styles["pause"] : styles["play"]} ${
        styles["play-pause-btn"]
      } button-reset`}
      onClick={handlePlayPause}
      role="button"
      aria-label={
        playing ? `${getLabel("pause")} Button` : `${getLabel("play")} Button`
      }
    >
      <ReactSVG
        src={playing ? getIcon("pause") : getIcon("play")}
        width={32}
        height={32}
        wrapper="svg"
        beforeInjection={(svg) => {
          svg.setAttribute(
            "alt",
            playing ? `${getLabel("pause")} Icon` : `${getLabel("play")} Icon`,
          );
          svg.setAttribute(
            "aria-label",
            playing ? `${getLabel("pause")} Icon` : `${getLabel("play")} Icon`,
          );
          svg.setAttribute("aria-hidden", `true`);
        }}
        aria-hidden
      />
    </button>
  );
};
